import type { Index } from '@/shared/Index'
import type { Session } from 'next-auth'
import type { HitV1, QueryV1, SortingV1, SubscriptionReference } from '@ttab/elephant-api/index'
import type { Repository } from '@/shared/Repository'
import { withStatus } from './withStatus'
import { withPlannings } from './withPlannings'
import type { useDocumentsFetchOptions } from '../'
import type { Dispatch, SetStateAction } from 'react'
import { asAssignments } from './asAssignments'
import type { Assignment } from '@/shared/schemas/assignments'
import { getDeliverableStatuses } from './getDeliverableStatuses'
import { withArticleFactboxes } from './withArticleFactboxes'

export async function fetch<T extends HitV1, F>({
  index,
  repository,
  session,
  query,
  page = 1,
  size = 100,
  documentType,
  fields,
  sort,
  loadDocument,
  setSubscriptions,
  setTotal,
  options
}: {
  index: Index | undefined
  session: Session | null
  repository?: Repository
  query?: QueryV1
  page?: number
  size?: number
  documentType: string
  fields?: F
  sort?: SortingV1[]
  /**
   * Return the whole newsdoc alongside the indexed fields, for a caller that
   * reads structure the flat fields cannot express - repeated blocks and what
   * belongs to which, the way the memo candidates read a planning's
   * assignments. It is a bigger response per hit, so it is asked for rather
   * than assumed.
   */
  loadDocument?: boolean
  setSubscriptions?: Dispatch<SetStateAction<SubscriptionReference[] | undefined>>
  setTotal?: Dispatch<SetStateAction<number | undefined>>
  options?: useDocumentsFetchOptions
}): Promise<T[]> {
  if (!index || !session?.accessToken) {
    throw new Error('Index or access token is missing')
  }

  // 'only' mode skips the standalone index query and returns article-embedded
  // factboxes alone. No standalone hits, no standalone subscription.
  const skipStandalone = options?.withArticleFactboxes === 'only'

  let result: T[] = []

  if (!skipStandalone) {
    const { ok, hits, errorMessage, subscriptions, total } = await index.query<T, F>({
      documentType,
      fields,
      accessToken: session.accessToken,
      query,
      size,
      page,
      sort,
      loadDocument,
      options
    })

    if (!ok) {
      throw new Error(errorMessage || 'Unknown error while fetching data')
    }

    if (subscriptions && subscriptions.length) {
      setSubscriptions?.(subscriptions)
    }

    setTotal?.(total)

    result = hits
  } else {
    // No standalone query means no hit count. Clear it rather than leaving the
    // previous mode's total in place for the caller to read as current.
    setTotal?.(undefined)
  }

  // Format planning result as assignments
  if (options?.asAssignments && query) {
    const statuses = await getDeliverableStatuses({ result, repository, session })
    return asAssignments(result as unknown as Assignment[], query, statuses) as unknown as T[]
  }
  // Append and format statuses
  if (options?.withStatus) {
    result = withStatus<T>(result)
  }

  // Append _relatedPlannings
  if (options?.withPlannings) {
    result = await withPlannings<T>({ hits: result, session, index })
  }

  if (options?.withArticleFactboxes) {
    result = await withArticleFactboxes<T>({ hits: result, session, index, query, repository, page, size })
  }


  return result
}
